// ============================================================================
// QuoteMakerNavBar
// ----------------------------------------------------------------------------
// The QuoteMaker top-nav strip (the whole dark industrial bar): the LEFT stage
// pills, the optional stage-nav context banner, the Stage 4/5 scope/quote
// Items+Total readout, the centre nav-message slot, and the RIGHT action
// cluster. Purely presentational -- it composes the already-extracted
// StagePills, StageNavContextBanner, NavScopeTotals, NavMessageBanner, and
// NavActions leaves and threads every state value / handler through as a prop
// named identically to its App identifier.
//
// App keeps ALL pipeline state plus the two chunky bits of logic that would
// otherwise drag ~17 more identifiers into this presentational layer:
//   - the StagePills click handler (passed as `onSelectStage`), and
//   - the NavScopeTotals gate + compute (passed as the display-ready
//     `navScopeTotals` object, so `fmtNavMoney` + the `*ForNav` derived totals
//     stay single-source in App).
//
// TS-060 / DEC-COUNCIL-015 T3 (QuoteMakerNavBar compose -- the final T3-NavBar
// step): the strip markup is moved VERBATIM from App.jsx's top-nav render
// (behaviour-preserving; only type annotations + this header added). No pipeline
// state moves in; this relocates coupling, it does not reduce it (real
// decoupling arrives with the T5+ pipeline-hook tranche).
// ============================================================================

import React from 'react';
import '../../styles/top-navbar.css';
import { StagePills } from './StagePills';
import { StageNavContextBanner } from './StageNavContextBanner';
import { NavScopeTotals } from './NavScopeTotals';
import { NavMessageBanner } from './NavMessageBanner';
import { NavActions } from './NavActions';

interface QuoteMakerNavBarProps {
    // --- StagePills (LEFT cluster) ---
    stage: number;
    quoteEditorOpen: boolean;
    quoteEditorSaveState?: { dirty?: boolean; saving?: boolean; saved?: boolean } | null;
    effectiveCompletedStages: Set<number>;
    /** Stages a Quote Brain job did not run, with the pill's explanation (StagePills notRunStages). */
    notRunStages?: Readonly<Record<number, string>> | null;
    quoteData: unknown;
    canNavigateToQuoteStage: (stageId: number) => boolean;
    isStageTransitioning: boolean;
    isAnalyzing: boolean;
    /** App owns the stage-pill click side effects; passed down verbatim. */
    onSelectStage: (stageId: number) => void;
    // --- StageNavContextBanner ---
    stageNavContext: { tone?: string; icon?: string; text: string } | null;
    // --- centre nav-message slot (shared gate + NavMessageBanner) ---
    navMessage: {
        tone?: string;
        icon?: string;
        text: string;
        id: string | number;
        dismissible?: boolean;
        action?: { onClick: () => void; busy?: boolean; label: string };
    } | null;
    dismissNavMessage: (id: string | number) => void;
    // --- NavScopeTotals (App owns the gate + compute; null when hidden) ---
    navScopeTotals: { items: number; totalLabel: string; totalDisplay: string; title: string } | null;
    // --- NavActions (RIGHT cluster) ---
    isSummarizing: boolean;
    isGenerating: boolean;
    photoAssignBusy: boolean;
    projectWMSGenerating: boolean;
    hasLivePipelineEntry: boolean;
    isSavingProject: boolean;
    hasUnsavedChanges?: boolean;
    onManualSave?: () => void;
    isStage1Processing: boolean;
    isFastTrackManualScope: boolean;
    /** Stage 1 on a Quote Brain job (quoteBrainStage1Route). */
    quoteBrainRoute?: 'writing' | 'written' | null;
    isNextStageJustReady: boolean;
    analysis: unknown;
    /** App's last-saved timestamp (a Date) or null; read only for truthiness in the auto-save gate. */
    lastSavedAt: Date | null;
    projectWMS: { structured?: unknown } | null | undefined;
    aiPipelineCancelToast: { message: string } | null;
    canGoNext: () => boolean;
    cancelAiPipeline: (opts: { reason: string }) => void;
    handleBack: () => void;
    handleNext: () => void;
    handleQuoteEditorBack: () => void;
    openNewProjectWorkflowChooser: () => void;
    quoteEditorActionsRef: React.MutableRefObject<{ save?: () => void }>;
}

export function QuoteMakerNavBar({
    stage,
    quoteEditorOpen,
    quoteEditorSaveState = null,
    effectiveCompletedStages,
    notRunStages = null,
    quoteData,
    canNavigateToQuoteStage,
    isStageTransitioning,
    isAnalyzing,
    onSelectStage,
    stageNavContext,
    navMessage,
    dismissNavMessage,
    navScopeTotals,
    isSummarizing,
    isGenerating,
    photoAssignBusy,
    projectWMSGenerating,
    hasLivePipelineEntry,
    isSavingProject,
    hasUnsavedChanges = false,
    onManualSave,
    isStage1Processing,
    isFastTrackManualScope,
    quoteBrainRoute = null,
    isNextStageJustReady,
    analysis,
    lastSavedAt,
    projectWMS,
    aiPipelineCancelToast,
    canGoNext,
    cancelAiPipeline,
    handleBack,
    handleNext,
    handleQuoteEditorBack,
    openNewProjectWorkflowChooser,
    quoteEditorActionsRef,
}: QuoteMakerNavBarProps) {
    return (
        <div
            className="qm-quote-nav-bar w-full items-center px-3 lg:px-4 shrink-0 relative"
            data-quote-editor-open={quoteEditorOpen ? 'true' : undefined}
            style={{ height: '72px', minHeight: '72px', maxHeight: '72px', boxSizing: 'border-box', background: 'linear-gradient(180deg, #1f1f1f 0%, #191919 100%)', borderBottom: '2px solid var(--qm-accent)', display: 'flex', flexWrap: 'nowrap', gap: '8px', alignItems: 'center', justifyContent: 'space-between', paddingTop: '8px', paddingBottom: '8px' }}
        >
            {/* A soft top-down gradient gives the strip depth. (The old 2px
                repeating-linear-gradient hatch was removed v5.46.x — on HiDPI
                displays the thin vertical lines aliased into visible shimmering
                stripes rather than the intended subtle texture.) */}

            {/* LEFT: Stage Navigation Buttons */}
            <StagePills
                stage={stage}
                quoteEditorOpen={quoteEditorOpen}
                completedStages={effectiveCompletedStages}
                notRunStages={notRunStages}
                quoteData={quoteData}
                canNavigateToStage={canNavigateToQuoteStage}
                isStageTransitioning={isStageTransitioning}
                isAnalyzing={isAnalyzing}
                onSelectStage={onSelectStage}
            />

            {/* CENTER: title + address moved to BottomNavBar (next to the
                page indicator). See BottomNavBar props projectTitle /
                projectAddress / onProjectTitleChange / onRegenerateTitle. */}

            {stageNavContext && !navMessage && (
                <StageNavContextBanner context={stageNavContext} />
            )}

            {/* Stage 4 + Stage 5 scope / quote Items + Total readout. App owns
                the visibility gate + the compute (so fmtNavMoney + the *ForNav
                derived totals stay single-source there) and passes the display-
                ready values via the navScopeTotals prop -- null when the readout
                should not render. */}
            {navScopeTotals && (
                <NavScopeTotals
                    items={navScopeTotals.items}
                    totalLabel={navScopeTotals.totalLabel}
                    totalDisplay={navScopeTotals.totalDisplay}
                    title={navScopeTotals.title}
                />
            )}

            {/* CENTRE: Nav-bar message slot. Stages push
                short status messages here via setNavMessage.
                Hidden below md so narrow viewports don't
                choke on the already-packed nav bar. */}
            {navMessage && (
                <NavMessageBanner message={navMessage} onDismiss={dismissNavMessage} />
            )}

            {/* RIGHT: Action Buttons */}
            <NavActions
                isAnalyzing={isAnalyzing}
                isSummarizing={isSummarizing}
                isGenerating={isGenerating}
                photoAssignBusy={photoAssignBusy}
                projectWMSGenerating={projectWMSGenerating}
                hasLivePipelineEntry={hasLivePipelineEntry}
                quoteEditorOpen={quoteEditorOpen}
                quoteEditorSaveState={quoteEditorSaveState}
                isSavingProject={isSavingProject}
                hasUnsavedChanges={hasUnsavedChanges}
                onManualSave={onManualSave}
                isStage1Processing={isStage1Processing}
                isFastTrackManualScope={isFastTrackManualScope}
                quoteBrainRoute={quoteBrainRoute}
                isNextStageJustReady={isNextStageJustReady}
                stage={stage}
                quoteData={quoteData}
                analysis={analysis}
                lastSavedAt={lastSavedAt}
                projectWMS={projectWMS}
                aiPipelineCancelToast={aiPipelineCancelToast}
                canGoNext={canGoNext}
                cancelAiPipeline={cancelAiPipeline}
                handleBack={handleBack}
                handleNext={handleNext}
                handleQuoteEditorBack={handleQuoteEditorBack}
                openNewProjectWorkflowChooser={openNewProjectWorkflowChooser}
                quoteEditorActionsRef={quoteEditorActionsRef}
            />
        </div>
    );
}
